'use client'

import { useEffect, useState } from 'react'
import posthog from 'posthog-js'
import { isNativePlatform, resetPlatformCache } from '@/lib/utils/platform'
import {
  DEFAULT_COOKIE_PREFERENCES,
  clearCookieChoice,
  getCookieConsent,
  getCookiePreferences,
  storeCookieChoice,
  type CookiePreferences,
} from '@/lib/analytics/consent'
import { applyBannerAnswer } from '@/lib/analytics/account-consent'

export type { CookiePreferences }

/*
 * The cookie-consent choice and what it does to PostHog (anonymous or full,
 * see lib/analytics/consent.ts). The classic banner
 * (components/cookie-consent.tsx) and the Encuentro one
 * (components/encuentro/frame/cookie-consent-banner.tsx) render the same
 * behavior from here; only the markup differs.
 *
 * Signed in, the banner's analytics answer is also the account's (Settings →
 * Privacy & Consent), see lib/analytics/account-consent.ts. The native app
 * never shows the banner; there the account's answer alone decides.
 */

export function useCookiePreferences(): CookiePreferences {
  const [prefs] = useState<CookiePreferences>(() => {
    // Lazy initialization to avoid hydration mismatch
    if (typeof window === 'undefined') return DEFAULT_COOKIE_PREFERENCES
    return getCookiePreferences()
  })

  return prefs
}

export interface CookieConsentBannerState {
  isVisible: boolean
  showDetails: boolean
  toggleDetails: () => void
  preferences: CookiePreferences
  setPreference: (key: 'functional' | 'analytics', value: boolean) => void
  acceptAll: () => void
  rejectNonEssential: () => void
  savePreferences: () => void
  close: () => void
}

/** State and handlers of the consent banner, whatever it looks like. */
export function useCookieConsentBanner(): CookieConsentBannerState {
  const [isVisible, setIsVisible] = useState(false)
  const [showDetails, setShowDetails] = useState(false)
  const [preferences, setPreferences] = useState<CookiePreferences>(() => {
    // Lazy initialization to load existing preferences
    if (typeof window === 'undefined') return DEFAULT_COOKIE_PREFERENCES
    return getCookiePreferences()
  })

  useEffect(() => {
    // No banner in the native apps (it wastes screen space there), so no
    // stored answer either: the account's answer decides there.
    // isNativePlatform() uses multiple detection strategies including
    // navigator.standalone for iOS WKWebView and user-agent for Android WebView.
    if (isNativePlatform()) return

    // In Capacitor remote mode, the JS bridge may not be injected yet when
    // React mounts. Wait briefly, reset the cached detection, and re-check
    // before deciding to show the banner.
    const bridgeCheckTimer = setTimeout(() => {
      resetPlatformCache()
      if (isNativePlatform()) return

      // Check if user has already consented
      const hasConsent = getCookieConsent()
      if (hasConsent === null) {
        // Show banner after a short delay for better UX
        setIsVisible(true)
      }
    }, 500)

    return () => clearTimeout(bridgeCheckTimer)
  }, [])

  // `decided`: a real answer, which signed in becomes the account's.
  const answer = (prefs: CookiePreferences, decided: boolean) => {
    storeCookieChoice(prefs)
    setPreferences(prefs)
    applyBannerAnswer(posthog, prefs.analytics, decided)
    setIsVisible(false)
  }

  const acceptAll = () => answer({ essential: true, functional: true, analytics: true }, true)

  const rejectNonEssential = () =>
    answer({ essential: true, functional: false, analytics: false }, true)

  const savePreferences = () => answer(preferences, true)

  // Closing is not an answer that allows analytics: it hides the banner and
  // keeps this browser's analytics off, whatever the switches show. It is not
  // an answer for the account either, which keeps what it had.
  const close = () => answer({ ...preferences, analytics: false }, false)

  return {
    isVisible,
    showDetails,
    toggleDetails: () => setShowDetails((shown) => !shown),
    preferences,
    setPreference: (key, value) => setPreferences((prev) => ({ ...prev, [key]: value })),
    acceptAll,
    rejectNonEssential,
    savePreferences,
    close,
  }
}

/**
 * Re-show the cookie consent banner (for use in settings)
 */
export function resetCookieConsent() {
  // Clear both layers so the banner reappears even on blocked-storage clients
  // where in-memory is the source of truth. The reload below makes this
  // belt-and-braces today, but keeps the reset honest if a future caller
  // wants to re-show the banner inline without a reload. The reload starts
  // PostHog anonymous again (no stored answer).
  clearCookieChoice()
  window.location.reload()
}
